import type { ChangeEvent, Dispatch, FC, SetStateAction } from 'react'
import {
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsIcon,
  CwsInput,
  CwsLabel,
  CwsSelect,
  CwsSelectOption,
  CwsTextarea,
} from '@components/cws'
import clsx from 'clsx'
import useSWRImmutable from 'swr/immutable'
import s from '@components/profile/Orders/ReturnOrder/ReturnOrder.module.scss'
import { useTranslate } from '@hooks/useTranslation'
import type { ReasonsActionsResponse } from '@pages/api/orders/return/reasons-actions'
import { useRouter } from 'next/router'

export type ReturnOrderItem = {
  orderNumber: string
  orderPlaced: string
  orderReceived: string
  itemName: string
  itemCode: string
  quantity: string
  reasonForReturn: string
  actionValue: string
  comment?: string
}
interface OrderDetailsProps {
  index: number
  setOrders: Dispatch<SetStateAction<ReturnOrderItem[]>>
  order: ReturnOrderItem
}

export const OrderDetails: FC<OrderDetailsProps> = ({ index, setOrders, order }) => {
  const { translate } = useTranslate()
  const { locale } = useRouter()
  const handleRemoveItem = () => {
    setOrders((prevState) => prevState.filter((item, idx) => idx !== index))
  }
  const { data: reasonsAndActions } = useSWRImmutable<ReasonsActionsResponse>('/api/orders/return/reasons-actions')

  const handleChangeOrder = (field: keyof ReturnOrderItem) => (value: string) => {
    setOrders((prevState) => {
      const currentOrders = [...prevState]
      currentOrders[index][field as keyof ReturnOrderItem] = value

      return currentOrders
    })
  }
  const handleChangeReasons = (reason: string) => {
    setOrders((prevState) => {
      const currentOrders = [...prevState]
      currentOrders[index]['reasonForReturn'] = reason
      return currentOrders
    })
  }
  const handleChangeActions = (reason: string) => {
    setOrders((prevState) => {
      const currentOrders = [...prevState]
      currentOrders[index]['actionValue'] = reason
      return currentOrders
    })
  }

  return (
    <div className="flex cws-bg--color-white">
      <div className="cws-py-xs">
        <CwsGrid>
          <CwsGridRow>
            <CwsGridCol col="12" lg="6">
              <CwsLabel>{`${translate('returns.email.form.orderNr')}:`}</CwsLabel>
              <CwsInput onChange={handleChangeOrder('orderNumber')} value={order.orderNumber} />
            </CwsGridCol>
            <CwsGridCol col="6" lg="3">
              <CwsLabel>{`${translate('return.form.order.date')}:`}</CwsLabel>
              <CwsInput
                onChange={handleChangeOrder('orderPlaced')}
                value={order.orderPlaced}
                placeholder="dd.mm.yyyy"
              />
            </CwsGridCol>
            <CwsGridCol col="6" lg="3">
              <CwsLabel>{`${translate('return.form.receive.date')}:`}</CwsLabel>
              <CwsInput
                onChange={handleChangeOrder('orderReceived')}
                value={order.orderReceived}
                placeholder="dd.mm.yyyy"
                name="orderReceived"
              />
            </CwsGridCol>
            <CwsGridCol col="12" lg="6">
              <CwsLabel>{`${translate('return.form.item.name')}:`}</CwsLabel>
              <CwsInput onChange={handleChangeOrder('itemName')} value={order.itemName} />
            </CwsGridCol>
            <CwsGridCol col="6" lg="3">
              <CwsLabel>{`${translate('return.form.item.code')}:`}</CwsLabel>
              <CwsInput onChange={handleChangeOrder('itemCode')} value={order.itemCode} />
            </CwsGridCol>
            <CwsGridCol col="6" lg="3">
              <CwsLabel>{`${translate('return.form.item.qty')}:`}</CwsLabel>
              <CwsInput onChange={handleChangeOrder('quantity')} value={order.quantity} type="number" />
            </CwsGridCol>
            <CwsGridCol col="12" lg="6">
              <CwsLabel>{`${translate('return.form.item.reason')}:`}</CwsLabel>
              <CwsSelect value={order.reasonForReturn} onChange={handleChangeReasons} placeholder="">
                {reasonsAndActions?.reasons.map((reason) => (
                  <CwsSelectOption
                    key={reason.code}
                    value={reason.code}
                    title={translate(`return.reason.${reason.code}`)}
                  />
                ))}
              </CwsSelect>
            </CwsGridCol>
            <CwsGridCol col="12" lg="6">
              <CwsLabel>{`${translate('return.form.item.action')}:`}</CwsLabel>
              <CwsSelect value={order.actionValue} onChange={handleChangeActions} placeholder="">
                {reasonsAndActions?.actions.map((action) => (
                  <CwsSelectOption
                    key={action.code}
                    value={action.code}
                    title={translate(`return.action.${action.code}`)}
                  />
                ))}
              </CwsSelect>
            </CwsGridCol>
            <CwsGridCol col="12">
              <CwsLabel>{`${translate('return.form.item.comment')}:`}</CwsLabel>
              <CwsTextarea onChange={handleChangeOrder('comment')} locale={locale} optional />
            </CwsGridCol>
          </CwsGridRow>
        </CwsGrid>
      </div>

      <div className={clsx(' flex justify-center cws-pt-s', s.removeBlock)}>
        <div className="cursor-pointer " onClick={handleRemoveItem}>
          <CwsIcon color="dark-grey" icon="close" size="16px" />
        </div>
      </div>
    </div>
  )
}
